import {
  AbstractType,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';
import { CountryFormatService } from '../country-format/country-format.service';
import { get, isFunction, merge, omit } from 'lodash';
import {
  floatNumberRoundingFormat,
  getNumberFormatOptionsWithDefault,
} from '@app/shared/utils/rounding-utils';
import { ValueFormatterParams } from 'ag-grid-community';
import {
  INumberFormatOptions,
  IRoundingFormatOptions,
  PartialRoundingFormatOptions,
} from './number-formatter.type';
import {
  DEFAULT_DISPLAY_DECIMAL,
  NEGATIVE_NUMBER_NEGATIVES_MAPPING,
  NegativeNumberType,
} from './number-format.constants';

/*
  NumberFormatService
  should includes all methods to format and display number-specific values
*/
export type ValueFormatterFn = (value: unknown) => string;

@Injectable()
export class NumberFormatService {
  /**
   * currency options
   */
  public formatOpts: IRoundingFormatOptions;

  public constructor(
    public countryFormatService: CountryFormatService,
    public injector: Injector,
  ) {
    this.formatOpts = getNumberFormatOptionsWithDefault({
      precision: this.getCurrentPrecision(),
      negatives: this.countryFormatService.getNegatives(),
      thousandsSeparator: this.countryFormatService.getThousandsSeparator(),
      decimalSeparator: this.countryFormatService.getDecimalSeparator(),
    });
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  /**
   * number parser with precision
   *
   * @param value target value
   * @param precision how many decimals places you want to format
   * @returns number string after precision round(will add trailingZeros)
   */
  public numberWithPrecisionParser(
    value: number,
    precision: number = this.formatOpts.precision,
  ): string {
    return floatNumberRoundingFormat(
      value?.toString(),
      merge({}, this.formatOpts, {
        precision,
        negatives: NEGATIVE_NUMBER_NEGATIVES_MAPPING[NegativeNumberType.Common],
      }),
    );
  }

  /**
   * number formatter
   *
   * @param isCurrency is need add currency
   * @param getNegativeNumberType get negative number type(is highest priority)
   * @param formatOpts number format options
   * @returns formatted param value
   */
  public getNumberFormatter(
    getNegativeNumberType: () => NegativeNumberType = null,
    formatOpts: INumberFormatOptions = this.formatOpts,
  ): ValueFormatterFn {
    const specOpts: PartialRoundingFormatOptions = {
      ...omit(formatOpts, 'onlyKeepValidDecimals'),
      defaultFormatValue: get(
        formatOpts,
        'defaultFormatValue',
        DEFAULT_DISPLAY_DECIMAL,
      ),
      precision: this.getCurrentPrecision(),
    };
    if (isFunction(getNegativeNumberType)) {
      specOpts.negatives =
        NEGATIVE_NUMBER_NEGATIVES_MAPPING[getNegativeNumberType()];
    }

    // return value formatter function
    return (params: ValueFormatterParams): string => {
      let parsedValue = parseFloat(params.value);
      // handle received parsed value as NaN, roundingFormatMethod value receive type are
      // string or number
      if (isNaN(parsedValue)) parsedValue = 0;
      const roundedValue = floatNumberRoundingFormat(
        parsedValue,
        merge({}, this.formatOpts, specOpts),
      );

      return roundedValue;
    };
  }

  public getCurrentPrecision(): number {
    return this.countryFormatService.getQuantityPrecision();
  }
}
